import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Target, Plus, Copy, X, CheckCircle2, Clock, ThumbsUp, ThumbsDown } from "lucide-react";

const TYPES = [
  "NCAA Division I", "NCAA Division II", "NCAA Division III", "NAIA", "JUCO", "U SPORTS", "CCAA",
  "Professional Scout", "MLB Organization", "Provincial Program", "National Team Program", "Academy",
  "Club / Travel Organization", "Independent Scout", "Other",
];
const EMPTY = { name: "", email: "", organization: "", recruiter_type: "NCAA Division I", title: "", phone: "" };

export default function AdminRecruiters() {
  const [items, setItems] = useState([]);
  const [requests, setRequests] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/admin/recruiters").then(({ data }) => setItems(data.items)).catch(() => {});
    api.get("/admin/recruiter-requests").then(({ data }) => {
      setRequests(data.items);
      window.dispatchEvent(new CustomEvent("cbr:recruiter-requests-changed"));
    }).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const approve = async (r) => {
    try { await api.post(`/admin/recruiter-requests/${r.id}/approve`); toast.success(`${r.name} approved as recruiter`); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Failed"); }
  };
  const reject = async (r) => {
    try { await api.post(`/admin/recruiter-requests/${r.id}/reject`, {}); toast.success(`${r.name}'s request rejected`); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Failed"); }
  };

  const create = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error("Name and email are required"); return; }
    setBusy(true);
    try { await api.post("/admin/recruiters", form); toast.success("Recruiter created"); setForm(EMPTY); setShowForm(false); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  const invite = async (r) => {
    try { const { data } = await api.post(`/admin/recruiters/${r.id}/invite`, {}); navigator.clipboard?.writeText(data.claim_link).catch(() => {}); toast.success("Invite created — link copied"); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Failed"); }
  };

  return (
    <AdminLayout>
      <div className="p-8 max-w-6xl">
        <div className="flex items-center justify-between">
          <div>
            <div className="label-overline text-[#E63946] flex items-center gap-2"><Target className="w-4 h-4" /> Recruiter Portal</div>
            <h1 data-testid="admin-recruiters-title" className="font-display text-4xl uppercase tracking-tighter mt-2">Recruiters</h1>
          </div>
          <button data-testid="add-recruiter-btn" onClick={() => setShowForm(true)} className="inline-flex items-center gap-2 bg-[#E63946] text-white px-4 py-2.5 text-xs font-semibold uppercase tracking-wider rounded-sm"><Plus className="w-4 h-4" /> Add Recruiter</button>
        </div>

        {requests.length > 0 && (
          <div className="mt-6 bg-white border-2 border-amber-300 rounded-sm overflow-hidden" data-testid="recruiter-requests-panel">
            <div className="px-4 py-3 bg-amber-50 border-b border-amber-200 flex items-center gap-2">
              <Clock className="w-4 h-4 text-amber-700" />
              <span className="label-overline text-amber-800">Pending Recruiter Requests</span>
              <span className="text-[10px] font-bold bg-amber-600 text-white px-2 py-0.5 rounded-full">{requests.length}</span>
            </div>
            <div className="divide-y divide-slate-100">
              {requests.map((r) => (
                <div key={r.id} data-testid={`recruiter-request-${r.id}`} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
                  <div>
                    <div className="font-semibold">{r.name} <span className="text-[11px] text-slate-400 font-normal">{r.email}</span></div>
                    <div className="text-xs text-slate-500 mt-0.5">{r.recruiter_type}{r.organization ? ` · ${r.organization}` : ""}{r.title ? ` · ${r.title}` : ""}</div>
                    {r.message && <div className="text-xs text-slate-600 mt-1 italic">“{r.message}”</div>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button data-testid={`approve-recruiter-${r.id}`} onClick={() => approve(r)} className="inline-flex items-center gap-1.5 px-3 py-2 text-[10px] font-semibold uppercase tracking-wider bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm"><ThumbsUp className="w-3.5 h-3.5" /> Approve</button>
                    <button data-testid={`reject-recruiter-${r.id}`} onClick={() => reject(r)} className="inline-flex items-center gap-1.5 px-3 py-2 text-[10px] font-semibold uppercase tracking-wider border border-slate-300 hover:bg-slate-100 text-slate-700 rounded-sm"><ThumbsDown className="w-3.5 h-3.5" /> Reject</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-6 bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="bg-slate-50 border-b border-slate-200">
              <th className="text-left p-3 label-overline">Name</th><th className="text-left p-3 label-overline">Organization</th>
              <th className="text-left p-3 label-overline">Type</th><th className="text-left p-3 label-overline">Account</th>
              <th className="text-right p-3 label-overline">Actions</th></tr></thead>
            <tbody data-testid="recruiters-table">
              {items.map((r) => (
                <tr key={r.id} data-testid={`recruiter-row-${r.id}`} className="border-b border-slate-100 last:border-0">
                  <td className="p-3"><div className="font-semibold">{r.name}</div><div className="text-[11px] text-slate-400">{r.email}</div></td>
                  <td className="p-3">{r.organization || "—"}</td>
                  <td className="p-3 text-xs">{r.recruiter_type}</td>
                  <td className="p-3">
                    {r.linked ? <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider bg-emerald-100 text-emerald-700 px-2 py-1 rounded-sm"><CheckCircle2 className="w-3 h-3" /> Linked</span>
                      : r.pending_invite ? <span className="text-[10px] font-semibold uppercase tracking-wider bg-amber-100 text-amber-800 px-2 py-1 rounded-sm">Invite Pending</span>
                      : <span className="text-[10px] uppercase tracking-wider text-slate-400">No account</span>}
                  </td>
                  <td className="p-3">
                    <div className="flex items-center justify-end gap-2">
                      {!r.linked && !r.pending_invite && <button data-testid={`invite-recruiter-${r.id}`} onClick={() => invite(r)} className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider bg-[#0A192F] text-white rounded-sm">Invite</button>}
                      {r.pending_invite && <button data-testid={`copy-recruiter-link-${r.id}`} onClick={() => { navigator.clipboard?.writeText(r.pending_invite.claim_link); toast.success("Link copied"); }} className="inline-flex items-center gap-1 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider border border-slate-300 rounded-sm"><Copy className="w-3 h-3" /> Copy Link</button>}
                    </div>
                  </td>
                </tr>
              ))}
              {items.length === 0 && <tr><td colSpan="5" className="p-12 text-center text-sm text-slate-400">No recruiters yet. Add one to invite them to the portal.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="recruiter-form-modal">
          <div className="bg-white rounded-sm w-full max-w-lg">
            <div className="flex items-center justify-between p-5 border-b border-slate-200"><h3 className="font-display text-xl uppercase tracking-tight">Add Recruiter</h3><button data-testid="recruiter-form-close" onClick={() => setShowForm(false)} className="p-2 hover:bg-slate-100"><X className="w-5 h-5" /></button></div>
            <div className="p-5 grid sm:grid-cols-2 gap-3">
              <F label="Name *"><input data-testid="recruiter-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inp} /></F>
              <F label="Email *"><input data-testid="recruiter-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inp} /></F>
              <F label="Organization"><input data-testid="recruiter-org" value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} className={inp} /></F>
              <F label="Type"><select data-testid="recruiter-type" value={form.recruiter_type} onChange={(e) => setForm({ ...form, recruiter_type: e.target.value })} className={`${inp} bg-white`}>{TYPES.map((t) => <option key={t} value={t}>{t}</option>)}</select></F>
              <F label="Title"><input data-testid="recruiter-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inp} /></F>
              <F label="Phone"><input data-testid="recruiter-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inp} /></F>
            </div>
            <div className="p-5 border-t border-slate-200 flex justify-end gap-3">
              <button onClick={() => setShowForm(false)} className="px-5 py-2 border border-slate-300 text-sm font-semibold uppercase tracking-wider rounded-sm">Cancel</button>
              <button data-testid="recruiter-save" onClick={create} disabled={busy} className="px-5 py-2 bg-[#E63946] text-white text-sm font-semibold uppercase tracking-wider rounded-sm disabled:opacity-60">{busy ? "Saving…" : "Create Recruiter"}</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
const inp = "w-full px-3 py-2 border border-slate-300 rounded-sm text-sm";
function F({ label, children }) { return <div><label className="label-overline text-[10px]">{label}</label><div className="mt-1">{children}</div></div>; }
